import React from 'react'
import { Input, Select } from 'antd'
import { FormikHelpers, FormikState } from 'formik'
import { NumericFormat } from 'react-number-format'
import { AdminsFinanceCreateVendorRequest, ProvinceResponseItem } from 'generated/api'
import CardHeaderForm from 'components/card/CardHeaderForm'
import Card from 'components/card/Card'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import { classNameInput } from 'helpers/formik/classNameForm'

interface AddressInformationProps {
  state: FormikState<AdminsFinanceCreateVendorRequest>
  helpers: FormikHelpers<AdminsFinanceCreateVendorRequest>
  provincesAndCities: ProvinceResponseItem[]
}

export default function FormAddressInformation(props: AddressInformationProps) {
  const {
    state,
    helpers: { setFieldValue, setFieldTouched },
    provincesAndCities,
  } = props

  return (
    <Card className="rounded-xl py-6 px-0" dataTestId="form-address-information">
      <CardHeaderForm title="2. Informasi Alamat" />
      <Card.Body className="mt-0 px-6">
        <div className="flex flex-col gap-3 xl:gap-9">
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Provinsi</label>
            </div>
            <div className="xl:w-9/12 h-full">
              <Select
                data-testid="select-province"
                status={state.errors.province && state.touched.province ? 'error' : ''}
                virtual={false}
                className="selector h-full mb-1 xl:w-2/5 w-full"
                placeholder="Pilih Provinsi"
                onChange={value => {
                  setFieldTouched('province', true).then(() => {
                    setFieldValue('province', value)
                  })
                  setFieldValue('city', '')
                }}
                value={state.values.province ? state.values.province : undefined}
              >
                {provincesAndCities.map(province => {
                  return (
                    <Select.Option key={province.name} value={province.name}>
                      {province.name}
                    </Select.Option>
                  )
                })}
              </Select>
              {state.errors.province && state.touched.province ? (
                <ErrorHintForm
                  errorMessage={state.errors.province}
                  dataTestId="error-message-province"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Kota / Kabupaten
              </label>
            </div>
            <div className="xl:w-9/12 h-full">
              <Select
                status={state.errors.city && state.touched.city ? 'error' : ''}
                virtual={false}
                className="selector h-full mb-1 xl:w-2/5 w-full"
                placeholder="Pilih Kota"
                onChange={value => {
                  setFieldTouched('city', true).then(() => {
                    setFieldValue('city', value)
                  })
                }}
                value={state.values.city ? state.values.city : undefined}
              >
                {provincesAndCities
                  .find(province => province.name === state.values.province)
                  ?.cities.map(city => {
                    return (
                      <Select.Option
                        key={city.name}
                        value={city.name}
                        data-testid={`select_stock_location_city_${city.name}`}
                      >
                        {city.name}
                      </Select.Option>
                    )
                  })}
              </Select>
              {state.errors.city && state.touched.city ? (
                <ErrorHintForm errorMessage={state.errors.city} dataTestId="error-message-city" />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Alamat Lengkap
              </label>
            </div>
            <div className="xl:w-9/12">
              <div className="xl:w-2/5 w-full">
                <Input.TextArea
                  data-testid="input-address"
                  id="address"
                  name="address"
                  className={`mb-1 w-64 ${classNameInput('address', state)}`}
                  placeholder="Masukkan Alamat Lengkap..."
                  value={state.values.address}
                  onChange={e => {
                    setFieldTouched('address', true)
                    setFieldValue('address', e.target.value)
                  }}
                  status={state.errors.address && state.touched.address ? 'error' : ''}
                />
                {state.errors.address && state.touched.address ? (
                  <ErrorHintForm
                    errorMessage={state.errors.address}
                    dataTestId="error-message-address"
                  />
                ) : null}
              </div>
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Titik Koordinat Lokasi
              </label>
            </div>
            <div className="xl:w-9/12 xl:flex xl:gap-6">
              <div className="xl:w-1/2 w-full">
                <span className="mb-2 text-xs text-[#323539] block font-bold">Latitude</span>
                <NumericFormat
                  data-testid="input-latitude"
                  id="latitude"
                  name="latitude"
                  customInput={Input}
                  placeholder="Contoh: 0.5245415"
                  className={`mb-1 ${classNameInput('latitude', state)}`}
                  value={state.values.latitude}
                  onValueChange={val => {
                    setFieldValue('latitude', val.floatValue ?? '')
                  }}
                  status={state.errors.latitude && state.touched.latitude ? 'error' : ''}
                  allowNegative={true}
                />
                {state.errors.latitude && state.touched.latitude ? (
                  <ErrorHintForm
                    errorMessage={state.errors.latitude}
                    dataTestId="error-message-latitude"
                  />
                ) : null}
              </div>
              <div className="xl:w-1/2 w-full">
                <span className="mb-2 text-xs text-[#323539] block font-bold">Longitude</span>
                <NumericFormat
                  data-testid="input-longitude"
                  id="longitude"
                  name="longitude"
                  customInput={Input}
                  placeholder="Contoh: 0.5245415"
                  className={`mb-1 ${classNameInput('longitude', state)}`}
                  value={state.values.longitude}
                  onValueChange={val => {
                    setFieldValue('longitude', val.floatValue ?? '')
                  }}
                  status={state.errors.longitude && state.touched.longitude ? 'error' : ''}
                  allowNegative={true}
                />
                {state.errors.longitude && state.touched.longitude ? (
                  <ErrorHintForm
                    errorMessage={state.errors.longitude}
                    dataTestId="error-message-longitude"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
